import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { useMessagerieStore } from "@store/messagerieStore";
import { isGUEST } from "@helpers/accessHelpers";
import { communicationsApi } from "@services/api/organizations";
import { serviceboxProxy } from "@services/serviceboxProxy";
import ComponentsScreen from "@screens/ComponentsScreen";
import LoginScreen from "@screens/auth/LoginScreen";
import AcceptInvitationScreen from "@screens/auth/AcceptInvitationScreen";
import SideBar from "./SideBar";
import { ErrorProvider } from "context/ErrorContext";
import { NavigationProvider } from "context/NavigationContext";
import { NotificationProvider } from "context/NotificationContext";
import SettingsScreen from "@screens/SettingsScreen";
import ProjectsScreen from "@screens/ProjectsScreen";
import ProjectScreen from "@screens/ProjectScreen";
import PartnersScreen from "@screens/PartnersScreen";
import BroadcastsScreen from "@screens/BroadcastsScreen";
import EmailsSentScreen from "@screens/EmailsSentScreen";
import HomeScreen from "@screens/HomeScreen";
import ReceptionScreen from "@screens/reception/ReceptionScreen";
import AtelierScreen from "@screens/atelier/AtelierScreen";
import BookingDebugScreen from "@screens/BookingDebugScreen";
import LoadTestScreen from "@screens/LoadTestScreen";
import SignScreen from "@screens/SignScreen";
import TrackingScreen from "@screens/TrackingScreen";
import TrackingScreenV2 from "@screens/TrackingScreenV2";
import DevisScreen from "@screens/DevisScreen";
import WatermarkScreen from "@screens/WatermarkScreen";
import VehiculesScreen from "@screens/VehiculesScreen";
import MessagerieScreen from "@screens/MessagerieScreen";
import DossiersScreen from "@screens/DossiersScreen";
import DossierDetailScreen from "@screens/DossierDetailScreen";
import OrdresScreen from "@screens/OrdresScreen";
import OrdreDetailScreen from "@screens/OrdreDetailScreen";
import CatalogScreen from "@screens/CatalogScreen";
import MobileLoginScreen from "@screens/mobile/MobileLoginScreen";
import MobileOrdresScreen from "@screens/mobile/MobileOrdresScreen";
import MobileOrdreDetailScreen from "@screens/mobile/MobileOrdreDetailScreen";
import MobilePlanningScreen from "@screens/mobile/MobilePlanningScreen";
import PrivacyPolicyScreen from "@screens/PrivacyPolicyScreen";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

/** Redirects small screens to /m unless already on a mobile route */
function MobileRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  if (isMobile && !location.pathname.startsWith("/m")) {
    return <Navigate to="/m" replace />;
  }
  return null;
}

/** Redirects large screens back from /m to desktop view */
function DesktopRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  if (!isMobile && location.pathname.startsWith("/m")) {
    return <Navigate to="/" replace />;
  }
  return null;
}

/** On small screens, redirect /login → /m (mobile login) */
function MobileLoginRedirect() {
  const isMobile = useIsMobile();
  if (isMobile) return <Navigate to="/m" replace />;
  return <LoginScreen />;
}

const PROXY_OFFLINE_THRESHOLD_MS = 10 * 60 * 1000; // matches backend OFFLINE_THRESHOLD_MINUTES
const PROXY_REFRESH_INTERVAL_MS = 2 * 60 * 1000;   // re-fetch orgData every 2 min to refresh proxy_last_seen
const PROXY_TICK_INTERVAL_MS = 30 * 1000;          // re-render every 30s so the elapsed check stays current

function ProxyOfflineBanner() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);
  const [, setTick] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // A registered proxy_url means a proxy exists and should be monitored,
  // even if the `enabled` flag is off — keeps in-app alerting in sync with
  // the backend health-check and the admin dashboard.
  const isMonitored = !!orgData?.servicebox?.proxy_url;

  useEffect(() => {
    if (!isMonitored) return;
    const refreshId = setInterval(() => {
      if (orgData?.id) fetchOrgData(orgData.id).catch(() => {});
    }, PROXY_REFRESH_INTERVAL_MS);
    const tickId = setInterval(() => setTick((n) => n + 1), PROXY_TICK_INTERVAL_MS);
    return () => {
      clearInterval(refreshId);
      clearInterval(tickId);
    };
  }, [orgData?.id, isMonitored, fetchOrgData]);

  const sb = orgData?.servicebox;
  if (!sb?.proxy_url || !sb.proxy_last_seen || dismissed) return null;
  const elapsedMs = Date.now() - new Date(sb.proxy_last_seen).getTime();
  if (elapsedMs < PROXY_OFFLINE_THRESHOLD_MS) return null;

  const elapsedMin = Math.floor(elapsedMs / 60000);
  const elapsedStr = elapsedMin < 60
    ? `${elapsedMin} min`
    : `${Math.floor(elapsedMin / 60)}h ${String(elapsedMin % 60).padStart(2, "0")}min`;

  // Floating top-right card with an indeterminate loading bar — reassures that we're
  // waiting for the proxy to come back, rather than a loud full-width error banner.
  return (
    <div
      style={{
        position: "fixed", top: 16, right: 16, zIndex: 9990,
        width: 320, maxWidth: "calc(100vw - 32px)",
        background: "#fff", borderRadius: 12, overflow: "hidden",
        boxShadow: "0 8px 24px rgba(0,0,0,0.16)", border: "1px solid #FCA5A5",
      }}
    >
      <style>{`@keyframes proxyIndeterminate {0%{left:-40%;width:40%}50%{left:30%;width:55%}100%{left:100%;width:40%}}`}</style>
      <div className="flex flex-row items-start gap-3" style={{ padding: "14px 14px 12px" }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="10" cy="10" r="9" fill="#DC2626" />
          <path d="M10 5v6M10 13.5v.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#111827" }}>
            En attente du redémarrage du proxy…
          </div>
          <div className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
            Hors ligne depuis {elapsedStr} — transferts Stellantis suspendus. Reconnexion en cours.
          </div>
        </div>
        <button onClick={() => setDismissed(true)} className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div style={{ position: "relative", height: 3, background: "#FEE2E2", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, height: "100%", background: "#DC2626", borderRadius: 3, animation: "proxyIndeterminate 1.4s ease-in-out infinite" }} />
      </div>
    </div>
  );
}

// Client-side reachability: the proxy can be up (backend heartbeat) yet THIS browser
// is blocked from reaching it — typically the "Local Network Access" / Private Network
// permission (HTTPS site → local server). Detect by pinging the proxy directly and show a
// banner with a button that re-attempts the connection (a user gesture triggers the prompt).
function ProxyLocalAccessBanner() {
  const orgData = useDataStore((s) => s.orgData);
  const sb = orgData?.servicebox;
  const [reachable, setReachable] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const monitored = !!sb?.proxy_url;
  const backendOnline = !!sb?.proxy_last_seen &&
    Date.now() - new Date(sb.proxy_last_seen).getTime() < PROXY_OFFLINE_THRESHOLD_MS;

  useEffect(() => {
    if (!monitored) return;
    let cancelled = false;
    const ping = async () => {
      const h = await serviceboxProxy.checkHealth();
      if (!cancelled) setReachable(!!h);
    };
    ping();
    const id = setInterval(ping, PROXY_TICK_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [monitored, sb?.proxy_url]);

  const requestAccess = async () => {
    setChecking(true);
    // User-gesture fetch to the local proxy → Chrome shows the "accès au réseau local" prompt.
    const h = await serviceboxProxy.checkHealth();
    setReachable(!!h);
    setChecking(false);
    if (h) setDismissed(true);
  };

  // Only when the proxy is up per the backend, but this browser can't reach it.
  if (!monitored || dismissed || !backendOnline || reachable !== false) return null;

  return (
    <div className="flex flex-row items-center gap-3 px-4 py-3" style={{ background: "#FEF3C7", borderBottom: "1px solid #F59E0B" }}>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2L1 18h18L10 2z" fill="#F59E0B" stroke="#D97706" strokeWidth="1" />
        <path d="M10 8v4M10 14.5v.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <span className="text-sm font-medium flex-1" style={{ color: "#92400E" }}>
        Ce navigateur ne parvient pas à communiquer avec le proxy ServiceBox sur cet ordinateur — autorisez l'accès au réseau local pour activer les transferts.
      </span>
      <button
        onClick={requestAccess}
        disabled={checking}
        className="flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium"
        style={{ background: "#D97706", color: "#fff", cursor: checking ? "default" : "pointer", opacity: checking ? 0.7 : 1 }}
      >
        {checking ? "Vérification…" : "Autoriser l'accès"}
      </button>
      <button onClick={() => setDismissed(true)} className="flex-shrink-0 p-1 rounded hover:bg-amber-200 transition-colors">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1l12 12M13 1L1 13" stroke="#92400E" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

function MainLayout() {
  const initGlobal = useMessagerieStore((s) => s.initGlobal);
  const orgMembers = useDataStore((s) => s.orgMembers);
  const [lowBalance, setLowBalance] = useState<number | null>(null);
  const [balanceDismissed, setBalanceDismissed] = useState(false);

  useEffect(() => {
    initGlobal();
  }, [orgMembers]);

  useEffect(() => {
    communicationsApi.getSpending().then((s) => {
      if (s.solde < 6) setLowBalance(s.solde);
    }).catch(() => {});
  }, []);

  return (
    <div className="flex flex-row overflow-x-hidden">
      <MobileRedirect />
      <SideBar />
      <div className="flex-1 min-w-0 relative flex flex-col">
        <ProxyOfflineBanner />
        <ProxyLocalAccessBanner />
        {lowBalance !== null && !balanceDismissed && (
          <div className="flex flex-row items-center gap-3 px-4 py-3" style={{ background: "#FEF3C7", borderBottom: "1px solid #F59E0B" }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L1 18h18L10 2z" fill="#F59E0B" stroke="#D97706" strokeWidth="1"/>
              <text x="10" y="15" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#92400E">!</text>
            </svg>
            <span className="text-sm font-medium flex-1" style={{ color: "#92400E" }}>
              Solde insuffisant ({lowBalance.toFixed(2)} EUR) — Les messages WhatsApp, emails, signatures et extractions IA pourraient être bloqués. Rechargez votre solde dans Paramètres {">"} Facturation.
            </span>
            <button onClick={() => setBalanceDismissed(true)} className="flex-shrink-0 p-1 rounded hover:bg-amber-200 transition-colors">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="#92400E" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
}

export default function Navigation() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const userData = useDataStore((s) => s.userData);
  const isGuest = isGUEST(userData);

  if (loading) {
    return <div className="bg-neutral-50 h-screen w-screen" />; // Loading state
  }

  return (
    <div className="bg-neutral-50">
      <ErrorProvider>
        <NotificationProvider>
          <BrowserRouter>
            <NavigationProvider>
              <Routes>
                {/* Public routes — accessible without authentication */}
                <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                <Route path="/sign/:token" element={<SignScreen />} />
                <Route path="/devis/:token" element={<DevisScreen />} />
                <Route path="/tracking/:id" element={<TrackingScreenV2 />} />
                <Route path="/tracking-v1/:id" element={<TrackingScreen />} />
                <Route path="/watermark" element={<WatermarkScreen />} />
                <Route path="/accept-invitation" element={<AcceptInvitationScreen />} />
                {user ? (
                  <>
                    {/* Mobile routes — no sidebar */}
                    <Route path="/m" element={<><DesktopRedirect /><MobileOrdresScreen /></>} />
                    <Route path="/m/planning" element={<><DesktopRedirect /><MobilePlanningScreen /></>} />
                    <Route path="/m/ordre/:id" element={<><DesktopRedirect /><MobileOrdreDetailScreen /></>} />

                    <Route element={<MainLayout />}>
                      <Route
                        path="/"
                        element={<Navigate to="/home" replace />}
                      />
                      <Route path="/home" element={<HomeScreen />} />
                      <Route path="/reception" element={<ReceptionScreen />} />
                      <Route path="/atelier" element={<AtelierScreen />} />
                      <Route path="/dossiers" element={<DossiersScreen />} />
                      <Route path="/dossier/:id" element={<DossierDetailScreen />} />
                      <Route path="/ordres" element={<OrdresScreen />} />
                      <Route path="/ordre/:id" element={<OrdreDetailScreen />} />
                      <Route path="/vehicules" element={<VehiculesScreen />} />
                      <Route path="/catalog" element={<CatalogScreen />} />
                      <Route path="/messagerie" element={<MessagerieScreen />} />
                      {/* Legacy Hexaflow routes — kept for template reuse
                      <Route path="/projects" element={<ProjectsScreen />} />
                      <Route path="/project/:id" element={<ProjectScreen />} />
                      <Route
                        path="/partners"
                        element={isGuest ? <Navigate to="/projects" replace /> : <PartnersScreen />}
                      />
                      <Route
                        path="/broadcasts"
                        element={isGuest ? <Navigate to="/projects" replace /> : <BroadcastsScreen />}
                      />
                      <Route
                        path="/emails-sent"
                        element={isGuest ? <Navigate to="/projects" replace /> : <EmailsSentScreen />}
                      />
                      */}
                      <Route path="/booking-debug" element={<BookingDebugScreen />} />
                      <Route path="/load-test" element={<LoadTestScreen />} />
                      <Route path="/settings" element={<SettingsScreen />} />
                      <Route
                        path="/components"
                        element={isGuest ? <Navigate to="/projects" replace /> : <ComponentsScreen />}
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/home" replace />}
                      />
                    </Route>
                  </>
                ) : (
                  <>
                    <Route path="/m" element={<MobileLoginScreen />} />
                    <Route path="/m/*" element={<MobileLoginScreen />} />
                    <Route path="/login" element={<MobileLoginRedirect />} />
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  </>
                )}
              </Routes>
            </NavigationProvider>
          </BrowserRouter>
        </NotificationProvider>
      </ErrorProvider>
    </div>
  );
}
